import * as React from "react";
import * as ReactDOM from "react-dom";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { DirectionalHint } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { elementContains, KeyCodes, WindowProvider } from "@fluentui/react";

import type {
  FunctionOrValue,
  SetTooltipProps,
  UILocation,
} from "@1js/acui-common";
import {
  getHoverableTooltipsPreference,
  getRibbonTextContainerSelector,
  shyComponentChangeEventName,
  hoverableTooltipMouseEnteredEventName,
  hoverableTooltipMouseLeaveEventName,
} from "@1js/acui-common";
import { AppContext, makeAppContextValue } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

import { AppTooltip } from "../AppTooltip";
import type { AppTooltipManagerProps } from "./AppTooltipManager.Props";

/**
 * overwriteTooltip() calls a render and eventually forces a style recalc.
 * This style recalc should be localized to only the tooltip component, and not the rest of the app.
 * Using CSS contain here ensures that the style recalc only recalculates the necessary styles.
 * In particular, Outlook Web had an issue where tooltips had style recalcs with thousands of elements.
 */
const overwriteTooltipStyle: React.CSSProperties = {
  contain: "content",
};

declare let __Delay_FluentV8_Tooltip__: boolean;

const V8AppTooltip =
  typeof __Delay_FluentV8_Tooltip__ === "undefined" ||
  !__Delay_FluentV8_Tooltip__
    ? AppTooltip
    : React.lazy(
        () => import(/* webpackChunkName: "commonV8" */ "../AppTooltip")
      );

export class AppTooltipManager extends React.PureComponent<
  React.PropsWithChildren<AppTooltipManagerProps>,
  {}
> {
  /* The DOM element that the tooltip will mount to */
  private tooltipWrapper = React.createRef<HTMLDivElement>();
  private tooltipWrapperRoot: Root | undefined = undefined;

  /* The DOM element that createPortal will render the tooltip to (appended to document.body for z-index) */
  private tooltipPortalContainer: HTMLDivElement | undefined = undefined;

  /* The DOM element that the tooltip's callout will target */
  private tooltipElement: HTMLElement | undefined;

  /* A unique identifier for the tooltip */
  private tooltipId: string | undefined;

  /* The text or function returning the text displayed on the tooltip */
  private tooltipLabel: FunctionOrValue<string> | undefined;

  /* Margin applied to the top of the tooltip. This is necessary for preview gallery
    buttons, which sit a few pixels above all other controls in the bottom bar. */
  private tooltipTopMargin: number | undefined;

  /* True if top level tooltips should be hidden. This occurs when a menu is dropped in the bottom bar */
  private hideTopLevelTooltips: boolean = false;

  /* True if the queued tooltipElement is in a menu. Menu-based tooltips should still render when hideTopLevelTooltips is true */
  private isMenuComponent: boolean | undefined;

  /* Optional direction for the tooltip; default is BottomCenter */
  private tooltipDirection: DirectionalHint | undefined;

  /* Optional direction for the tooltip; default is undefined */
  private tooltipDirectionForRTL: DirectionalHint | undefined;

  /* The callback passed in to dismiss the tooltip */
  private dismissTooltipCallback?: () => void;

  /* True if the next tooltip should render with a delay */
  private delayTooltip: boolean = true;

  /* True if tooltip is already disabled */
  private isTooltipDisabled: boolean = false;

  /* Tracks the ID of the timeouts so that they can be cleared by another event */
  private timeoutID: number = 0;

  /* True if there is currently a tooltip on the screen. This accounts for the animation delay after a tooltip is mounted */
  private tooltipIsVisible: boolean = false;

  /* Tracks the ID of the hover timeouts so that they can be cleared by another event */
  private hoverTimeout: number = 0;

  /* Used for tracking if mouse has re-entered tooltip after getting a mouseLeave event */
  private mouseEntered: boolean = false;

  /* True if the hover timeout is what is causing the tooltip to dismiss */
  private dismissFromTimeout: boolean = false;

  /* Current UI location the given tooltip is being presented from */
  private location: UILocation | undefined = undefined;

  declare context: React.ContextType<typeof AppContext>;

  public render() {
    return (
      <AppContext.Consumer>
        {(context) => (
          <AppContext.Provider
            value={makeAppContextValue(
              context.isExperimentalFeatureEnabled,
              this.setTooltipElement,
              this.updateTooltipsHidden,
              context.appWindow,
              context.isPlatformFeatureEnabled,
              context.responsiveClassName,
              context.logger
            )}
          >
            {this.props.children}
            <div ref={this.tooltipWrapper} />
          </AppContext.Provider>
        )}
      </AppContext.Consumer>
    );
  }

  public componentDidMount() {
    const react18RenderEnabled = this.context.isExperimentalFeatureEnabled?.(
      ribbonExperiments.react18RenderTooltipFixEnabled
    );

    if (
      react18RenderEnabled &&
      this.tooltipWrapper.current &&
      this.tooltipWrapperRoot === undefined
    ) {
      this.tooltipWrapperRoot = createRoot(this.tooltipWrapper.current);
      // Create a separate container for portal rendering to ensure tooltip appears on top.
      // This container is appended to document.body to guarantee highest z-index stacking.
      this.tooltipPortalContainer = document.createElement("div");
      document.body.appendChild(this.tooltipPortalContainer);
    }
    // register for keydown events so we can dismiss tooltips on a shortcut key
    document.addEventListener("keydown", this.globalKeyDown, { capture: true });
    document.addEventListener(
      shyComponentChangeEventName,
      this.onShyComponentChange
    );
  }

  public componentWillUnmount() {
    document.removeEventListener("keydown", this.globalKeyDown, {
      capture: true,
    });
    document.removeEventListener(
      shyComponentChangeEventName,
      this.onShyComponentChange
    );

    // Clean up any rendered tooltips before the wrapper ref becomes null
    // This prevents dangling tooltips when child components try to unmount after this component
    const react18RenderEnabled = this.context?.isExperimentalFeatureEnabled?.(
      ribbonExperiments.react18RenderTooltipFixEnabled
    );
    const tooltipsNotDismissingAfterUnmountFixEnabled = this.context
      ? checkExperiment(
          this.context,
          ribbonExperiments.tooltipNotDismissingAfterUnmountFixEnabled
        )
      : false;

    if (tooltipsNotDismissingAfterUnmountFixEnabled) {
      // Fix for dangling tooltips after callout dismissal:
      // When AppTooltipManager unmounts (e.g., pressing ESC on BaseCallout), child components like AppIconButton
      // may try to clean up tooltips after tooltipWrapper.current becomes null, preventing proper cleanup.
      // Since tooltipWrapperRoot creates a separate React tree, and FluentUI's Tooltip uses portals to render
      // directly to some other part of DOM, the tooltip DOM persists even after tooltipWrapper is removed from the main tree.
      // Solution: Explicitly unmount the tooltipWrapperRoot React tree before this component unmounts,
      // ensuring all child portals (including FluentUI's Layer) are properly cleaned up from the DOM.
      if (react18RenderEnabled) {
        this.tooltipWrapperRoot?.unmount();
        this.tooltipWrapperRoot = undefined;
        // Clean up the portal container from document.body
        if (
          this.tooltipPortalContainer &&
          this.tooltipPortalContainer.parentNode
        ) {
          this.tooltipPortalContainer.parentNode.removeChild(
            this.tooltipPortalContainer
          );
          this.tooltipPortalContainer = undefined;
        }
      } else if (this.tooltipWrapper.current) {
        ReactDOM.unmountComponentAtNode(this.tooltipWrapper.current);
      }
    }
  }

  private getAppToolTip() {
    const hoverablePreference: boolean = getHoverableTooltipsPreference();

    const {
      isExperimentalFeatureEnabled,
      isPlatformFeatureEnabled,
      setTooltipElement,
      updateTooltipsHidden,
      appWindow,
    } = this.context;

    let tooltip = (
      <React.Suspense fallback={<></>}>
        <V8AppTooltip
          appColors={this.props.appColors}
          targetElement={this.tooltipElement}
          content={this.tooltipLabel}
          id={this.tooltipId}
          topMargin={this.tooltipTopMargin}
          delayTooltip={this.delayTooltip}
          hidden={!this.tooltipElement || !this.tooltipLabel || !this.tooltipId}
          directionalHint={this.tooltipDirection}
          directionalHintForRTL={this.tooltipDirectionForRTL}
          mouseLeaveCallback={
            hoverablePreference ? this.onMouseLeave : undefined
          }
          mouseEnterCallback={
            hoverablePreference ? this.onMouseEnter : undefined
          }
        />
      </React.Suspense>
    );

    if (
      isExperimentalFeatureEnabled?.(
        ribbonExperiments.appTooltipAppContextIsEnabled
      )
    ) {
      tooltip = (
        // `getAppToolTip` is called inside `ReactDOM.render` which creates a new component
        // instance (with no context). To workaround this, we manually re-create the `AppContext`
        // from `this.context`. Without this workaround, `AppContext` will unexpectedly be an empty
        // object inside `AppTooltip`.
        <AppContext.Provider
          value={makeAppContextValue(
            isExperimentalFeatureEnabled,
            setTooltipElement,
            updateTooltipsHidden,
            appWindow,
            isPlatformFeatureEnabled
          )}
        >
          {tooltip}
        </AppContext.Provider>
      );
    }

    if (appWindow) {
      return <WindowProvider window={appWindow}>{tooltip}</WindowProvider>;
    }

    return tooltip;
  }

  /**
   * Tooltips are rendered from an instance method so that we don't unnecessarily trigger renders of the ribbon
   */
  private renderTooltip = () => {
    // if disableToolTip is true, then overwrite any current tooltip and return with no operation
    if (this.props.disableTooltip) {
      !this.isTooltipDisabled && this.overwriteTooltip();
      this.isTooltipDisabled = true;
      return;
    } else if (this.isTooltipDisabled) {
      this.isTooltipDisabled = false;
    }

    this.tooltipIsVisible =
      !this.delayTooltip &&
      !!this.tooltipElement &&
      !!this.tooltipLabel &&
      !!this.tooltipId;
    this.delayTooltip && this.overwriteTooltip(); // To show the delay/animation, the tooltip must first be remounted
    if (
      this.tooltipWrapper.current &&
      (!this.hideTopLevelTooltips || this.isMenuComponent)
    ) {
      const react18RenderEnabled = this.context.isExperimentalFeatureEnabled?.(
        ribbonExperiments.react18RenderTooltipFixEnabled
      );
      if (react18RenderEnabled) {
        // Reuse existing root to avoid race conditions from synchronously unmounting while React is rendering.
        if (!this.tooltipWrapperRoot) {
          this.tooltipWrapperRoot = createRoot(this.tooltipWrapper.current);
        }
        // Ensure portal container exists (may not exist if root was created here instead of componentDidMount)
        if (!this.tooltipPortalContainer) {
          this.tooltipPortalContainer = document.createElement("div");
          document.body.appendChild(this.tooltipPortalContainer);
        }
        // Use createPortal with a SEPARATE container (tooltipPortalContainer) to ensure tooltip renders on top.
        // The portal container is appended to document.body, separate from the createRoot container.
        const portalTarget = this.tooltipPortalContainer;
        // To show the delay/animation, first render empty then render tooltip in next frame.
        // We can't use flushSync here as it may be called during React's render cycle.
        if (this.delayTooltip) {
          this.tooltipWrapperRoot.render(<div style={overwriteTooltipStyle} />);
          requestAnimationFrame(() => {
            this.tooltipWrapperRoot?.render(
              ReactDOM.createPortal(this.getAppToolTip(), portalTarget)
            );
          });
        } else {
          this.tooltipWrapperRoot.render(
            ReactDOM.createPortal(this.getAppToolTip(), portalTarget)
          );
        }
      } else {
        ReactDOM.render(
          getHoverableTooltipsPreference()
            ? ReactDOM.createPortal(
                this.getAppToolTip(),
                this.tooltipWrapper.current!
              )
            : this.getAppToolTip(),
          this.tooltipWrapper.current
        );
      }
    } else {
      this.overwriteTooltip();
    }
  };

  /**
   * If a tooltip is not set, an empty div is rendered in it's place
   */
  private overwriteTooltip = () => {
    if (!this.tooltipWrapper.current) {
      return;
    }

    const react18RenderEnabled = this.context.isExperimentalFeatureEnabled?.(
      ribbonExperiments.react18RenderTooltipFixEnabled
    );

    if (react18RenderEnabled) {
      // Render empty placeholder instead of unmounting to avoid race conditions
      // when React is still rendering. This matches the legacy behavior of rendering
      // an empty div.
      if (this.tooltipWrapperRoot) {
        this.tooltipWrapperRoot.render(<div style={overwriteTooltipStyle} />);
      }
      return;
    }

    ReactDOM.render(
      <div style={overwriteTooltipStyle} />,
      this.tooltipWrapper.current
    );
  };

  private clearTimeoutIfNeeded = (
    currentTooltipId?: string,
    newTooltipId?: string
  ): void => {
    // Clear the current tooltip timeout and dismissal info if:
    if (
      // the callback to set the tooltip is coming from the expired timeout
      this.dismissFromTimeout ||
      // or there timeout has expired and a new tooltip is showing/hiding
      ((this.hoverTimeout !== 0 || this.hoverTimeout !== undefined) &&
        currentTooltipId !== newTooltipId)
    ) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = 0;
      this.dismissFromTimeout = false;
    }
  };

  /**
   * This is the callback that each component uses to set a tooltip via the AppTooltipHost.
   * @param {HTMLElement} element - The DOM element that we'll anchor the tooltip to
   * @param {string} label - The string that will be displayed in the tooltip
   * @param {string} id - The id of the component that is hosting the tooltip
   * @param {number} tooltipTopMargin - Margin added to the top of the tooltip
   * @param {boolean} isMenuComponent - True if the element is inside a menu
   */
  private setTooltipElement = (tooltipProps: SetTooltipProps): void => {
    if (tooltipProps.element) {
      const textContainer = tooltipProps.element.querySelector(
        getRibbonTextContainerSelector(true /**useOptimizedSelectors*/)
      );
      const tooltipLabelValue =
        typeof tooltipProps.label === "function"
          ? tooltipProps.label()
          : tooltipProps.label;

      // If the label (e.g. textContainer) is the same as the tooltip value and is displayed, we need to not show the tooltip
      if (
        textContainer &&
        textContainer.textContent === tooltipLabelValue &&
        window.getComputedStyle(textContainer).display !== "none"
      ) {
        return;
      }
    }

    this.clearTimeoutIfNeeded(this.tooltipId, tooltipProps.id);
    if (tooltipProps.element) {
      tooltipProps.element.addEventListener(
        "mouseleave",
        this.onMouseLeave,
        false
      );
      tooltipProps.element.addEventListener(
        "mouseenter",
        this.onMouseEnter,
        false
      );
    } else if (this.tooltipElement) {
      this.tooltipElement.removeEventListener(
        "mouseleave",
        this.onMouseLeave,
        false
      );
      this.tooltipElement.removeEventListener(
        "mouseenter",
        this.onMouseEnter,
        false
      );
    }

    /**
     * Since menus exist on a different layer than the ribbon, we need to make sure that we render a new tooltip if this layer changes
     * This will prevent tooltips from being rendered behind menus
     */
    if (this.isMenuComponent !== tooltipProps.isMenuComponent) {
      this.delayTooltip = true;
    }

    this.tooltipElement = tooltipProps.element;
    this.tooltipLabel = tooltipProps.label;
    this.tooltipId = tooltipProps.id;
    this.tooltipTopMargin = tooltipProps.tooltipTopMargin;
    this.isMenuComponent = tooltipProps.isMenuComponent;
    this.tooltipDirection = tooltipProps.tooltipDirection;
    this.tooltipDirectionForRTL = tooltipProps.tooltipDirectionForRTL;
    this.dismissTooltipCallback = tooltipProps.dismissTooltipCallback;
    this.location = tooltipProps.location;

    this.setDelayTimeout();
    this.trackTooltipVisibility();
    this.renderTooltip();
  };

  /**
   * This callback is used when top level tooltips should be hidden. This is currently set to true when a menu is dropped.
   */
  private updateTooltipsHidden = (hideTopLevelTooltips: boolean) => {
    this.hideTopLevelTooltips = hideTopLevelTooltips;
    this.tooltipIsVisible = false;
    this.renderTooltip();
  };

  /**
   * If a tooltip is trigged within 500ms of a tooltip being show, we want to render the tooltip without a delay.
   */
  private setDelayTimeout = () => {
    if (this.tooltipIsVisible && !this.tooltipElement) {
      this.delayTooltip = false;
      clearTimeout(this.timeoutID);
      this.timeoutID = window.setTimeout(() => {
        this.delayTooltip = true;
        this.timeoutID = 0;
      }, 500);
    }
    this.tooltipIsVisible = this.tooltipElement ? this.tooltipIsVisible : false;
  };

  /**
   * The tooltip initially renders with a 500ms delay. This timout is used to track whether the tooltip is visible
   * on the screen. This is necessary in determining the delay for future tooltip renders.
   */
  private trackTooltipVisibility = () => {
    if (!this.tooltipIsVisible && this.tooltipElement) {
      clearTimeout(this.timeoutID);
      this.timeoutID = window.setTimeout(() => {
        this.tooltipIsVisible = true;
      }, 500);
    }
  };

  /**
   * If the user hits ctrl when a tooltip is open, close it
   */
  private globalKeyDown = (ev: KeyboardEvent): void => {
    if (
      this.tooltipIsVisible &&
      this.tooltipElement &&
      ev.ctrlKey &&
      !ev.shiftKey &&
      ev.which === KeyCodes.ctrl
    ) {
      ev.stopPropagation();
      this.dismissTooltipCallback && this.dismissTooltipCallback();
    }
  };

  /**
   * When a shy component changes state we should be dismissing tooltips
   */
  private onShyComponentChange = (event: CustomEvent) => {
    if (
      this.tooltipIsVisible &&
      this.tooltipElement &&
      this.dismissTooltipCallback &&
      event.detail.isShowing
    ) {
      this.dismissTooltipCallback();
    }
  };

  private isMouseWithinTooltipOrAnchor = (target: EventTarget): boolean => {
    // if the mouse did not go to the callout wrapper
    return (
      target === this.tooltipWrapper.current ||
      // and the mouse is not within the callout/tooltip
      elementContains(this.tooltipWrapper.current, target as HTMLElement) ||
      // and the mouse is not within the element
      target === this.tooltipElement ||
      // and the mouse is not within the element's children
      elementContains(this.tooltipElement as HTMLElement, target as HTMLElement)
    );
  };

  /**
   * Tracks the mouse events for the tooltip callout and the element
   * to not dismiss the tooltip when we hover over it
   */
  private onMouseLeave = (event: MouseEvent) => {
    if (!getHoverableTooltipsPreference()) {
      this.dismissTooltipCallback && this.dismissTooltipCallback();
      return;
    }

    if (
      event.relatedTarget &&
      !this.isMouseWithinTooltipOrAnchor(event.relatedTarget)
    ) {
      this.delayTooltip = false;
      this.mouseEntered = false;
      // if there is no current timeout tracking
      if (this.hoverTimeout === 0 || this.hoverTimeout === undefined) {
        const customEventDetails = {
          detail: {
            location: this.location,
            x: event.clientX,
            y: event.clientY,
          },
        };
        // set timeout for 500ms to delay dismissal to make it easier for users to hover
        this.hoverTimeout = window.setTimeout(() => {
          if (!this.mouseEntered) {
            this.dismissFromTimeout = true;
            this.dismissTooltipCallback && this.dismissTooltipCallback();

            document.dispatchEvent(
              new CustomEvent(
                hoverableTooltipMouseLeaveEventName,
                customEventDetails
              )
            );
          }
          this.hoverTimeout = 0;
        }, 500);
      }
    }
  };

  private onMouseEnter = (event: MouseEvent) => {
    if (!getHoverableTooltipsPreference()) {
      return;
    }

    // track if mouse has re-entered tooltip or anchor so we don't dismiss on timeout
    if (event.target && this.isMouseWithinTooltipOrAnchor(event.target)) {
      this.mouseEntered = true;

      document.dispatchEvent(
        new CustomEvent(hoverableTooltipMouseEnteredEventName, {
          detail: { location: this.location },
        })
      );
    }
  };
}

AppTooltipManager.contextType = AppContext;
